import { ColDef, GridOptions } from '@ag-grid-community/core'
import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { ClearwaterTransaction } from '@helios/common-api'
import {
  AccountInfo,
  RootState,
  selectFirstFocusedAccountInfo,
} from '@helios/state-management'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { AgGridApi } from '@northfork/ag-grid'
import { composeFieldPath } from '@northfork/basics'
import { ToastService } from '@northfork/ng-basics'
import { Subject } from 'rxjs'
import { distinctUntilChanged, takeUntil, tap } from 'rxjs/operators'
import { SleeveSimpleAccounts } from '../../models/move-to-different-sleeve.model'
import { ClearwaterTransactionsService } from '../../services/clearwater-transactions.service'
import { MoveToDifferentSleeveService } from '../../services/move-to-different-sleeve.service'

const field = composeFieldPath([] as SleeveSimpleAccounts[])

@Component({
  selector: 'hs-app-move-to-different-sleeve-modal',
  templateUrl: './move-to-different-sleeve-modal.component.html',
  styleUrls: ['./move-to-different-sleeve-modal.component.scss'],
})
export class MoveToDifferentSleeveModalComponent implements OnInit, OnDestroy {
  @Input() input?: ClearwaterTransaction[]

  gridApi?: AgGridApi
  subAccounts: SleeveSimpleAccounts[] = []
  sessionLoadedAccountInfo?: AccountInfo
  isLoadingSubSleeveAccounts = false
  formSubmitting = false
  disableSave = true
  destroyFirstFocusedAccountSubject = new Subject<void>()

  constructor(
    private readonly store: Store<RootState>,
    public toastService: ToastService,
    private readonly moveToDifferentSleeveService: MoveToDifferentSleeveService,
    private readonly clearwaterTransactionsService: ClearwaterTransactionsService,
    public activeModal: NgbActiveModal,
  ) {}

  private readonly columns: ColDef[] = [
    {
      field: field('id'),
      headerName: 'Account Id',
    },
    {
      field: field('name'),
      headerName: 'Account Name',
    },
  ]
  gridOptions: GridOptions = this.createGridOptions()

  createGridOptions(): GridOptions {
    return {
      columnDefs: this.columns,
      defaultColDef: {
        sortable: true,
        filter: true,
      },
      onGridReady: event => {
        this.gridApi = event
      },
      onSelectionChanged: () => {
        if ((this.gridApi as AgGridApi)?.api.getSelectedRows().length > 0)
          this.disableSave = false
        else this.disableSave = true
      },
      rowSelection: 'single',
      suppressRowClickSelection: false,
      suppressCopyRowsToClipboard: true,
      sideBar: false,
      enableRangeSelection: false,
    }
  }

  ngOnInit() {
    this.store
      .select(selectFirstFocusedAccountInfo)
      .pipe(
        takeUntil(this.destroyFirstFocusedAccountSubject),
        distinctUntilChanged(),
        tap(selectedAccount => {
          if (selectedAccount) {
            this.loadSubAccounts(selectedAccount)
          }
        }),
      )
      .subscribe()
  }

  ngOnDestroy(): void {
    this.destroyFirstFocusedAccountSubject.next()
    this.destroyFirstFocusedAccountSubject.complete()
    this.gridApi?.api?.destroy()
  }

  loadSubAccounts(sessionLoadedAccount: AccountInfo) {
    if ('sleeveAggregate' === sessionLoadedAccount.accountType) {
      this.isLoadingSubSleeveAccounts = true
      this.moveToDifferentSleeveService
        .fetchSubAccounts(sessionLoadedAccount.accountId)
        .subscribe(accounts => {
          const accountIdsToRemove = this.input?.map(input => input.accountId)
          this.subAccounts = accounts.filter(
            account =>
              account.id != sessionLoadedAccount.accountId &&
              !accountIdsToRemove?.includes(account.id),
          )
          this.isLoadingSubSleeveAccounts = false
        })
    }
  }

  save() {
    this.formSubmitting = true
    const destAccountId = this.gridApi?.api.getSelectedRows()[0]?.id
    if (this.input) {
      this.clearwaterTransactionsService
        .moveTransaction(this.input, destAccountId)
        .toPromise()
        .then(() => {
          this.formSubmitting = false
          this.toastService.success(
            'Move Transactions command is successfully submitted.',
          )
          this.activeModal.close()
        })
    }
  }
}
